import type { AuctionStatus, CurrentLotStatus } from '@/types/LiveAuctionState';
import { handleResponseOld, handleResponseWithNon200Errors, makeGet } from './helpers';
import { PubSubConfig, PubSubProvider } from '@/types/PubSubProvider';
import { ApprovalStatus } from '@/types/Approval';

/**
 * @category Clerk API
 * @see fetchLiveCatalogStatus
 */
type FetchLiveCatalogStatusParams = {
    authToken: string;
    catalogId: number;
    deployment: string;
    sellerId: number;
};

type FetchLiveCatalogStatusResponsePayload = {
    auctionId: number;
    auctionStatus: AuctionStatus;
    bidderApproval: ApprovalStatus;
    bidderHasApprovalStatus: boolean;
    currentAskPrice: number;
    currentHighBid: number;
    currentIncrement: number;
    currentLotId: number;
    currentLotIndex: number;
    currentLotNumber: number;
    currentLotStatus: CurrentLotStatus;
    leadingBidderId: number;
    messageProvider: PubSubProvider;
    subscriptions: {
        ably: PubSubConfig;
        pubnub: PubSubConfig;
    };
};

type TransformFetchLiveCatalogStatusResponsePayload = {
    payload: FetchLiveCatalogStatusResponsePayload;
};

export type FetchLiveAuctionStatusResponse = {
    data: {
        bidder: {
            approval: ApprovalStatus;
            hasApprovalStatus: boolean;
            messagingProvider: PubSubProvider;
            subscriptions: {
                ably: PubSubConfig;
                pubnub: PubSubConfig;
            };
        };
        catalogId: number;
        currentItem: {
            askPrice: number;
            increment: number;
            itemId: number;
            itemIndex: number;
            leadingBid: number;
            leadingBidder: number;
            lotNumber: number;
            lotStatus: CurrentLotStatus;
        };
        status: AuctionStatus;
    };
    error: boolean;
};

/**
 * Gets the live catalog status from auction engine
 * @function fetchLiveCatalogStatus
 * @category Clerk API
 * @param {FetchLiveCatalogStatusParams}
 * @returns {FetchLiveAuctionStatusResponse}
 * @see https://api-DEPLOYMENT.liveauctioneers.com/clerk/houses/${sellerId}/auctions/${catalogId}/state/bidder
 */
export const fetchLiveCatalogStatus = ({ authToken, catalogId, deployment, sellerId }: FetchLiveCatalogStatusParams) =>
    new Promise<FetchLiveAuctionStatusResponse>((resolve, reject) => {
        const request = makeGet({
            apiPath: `clerk/houses/${sellerId}/auctions/${catalogId}/state/bidder`,
            authToken,
            deployment,
            path: '<AUCTION-ENGINE-API>',
        });
        request.end((err: any, response: any) =>
            handleResponseWithNon200Errors({
                err,
                reject,
                resolve,
                response,
                success404: true,
                transform: ({
                    payload,
                }: TransformFetchLiveCatalogStatusResponsePayload): FetchLiveAuctionStatusResponse => {
                    const {
                        auctionStatus,
                        bidderApproval,
                        bidderHasApprovalStatus,
                        currentAskPrice,
                        currentHighBid,
                        currentIncrement,
                        currentLotId,
                        currentLotIndex,
                        currentLotNumber,
                        currentLotStatus,
                        leadingBidderId,
                        messageProvider,
                        subscriptions,
                    } = payload || {};

                    return {
                        data: {
                            bidder: {
                                approval: bidderApproval,
                                hasApprovalStatus: bidderHasApprovalStatus,
                                messagingProvider: messageProvider,
                                subscriptions,
                            },
                            catalogId,
                            currentItem: {
                                askPrice: currentAskPrice,
                                increment: currentIncrement,
                                itemId: currentLotId,
                                itemIndex: currentLotIndex,
                                leadingBid: currentHighBid,
                                leadingBidder: leadingBidderId,
                                lotNumber: currentLotNumber,
                                lotStatus: currentLotStatus,
                            },
                            status: auctionStatus,
                        },
                        error: false,
                    };
                },
            })
        );
    });

/**
 * @category Clerk API
 * @see fetchLiveCatalogStatusFromPubnub
 */
type FetchCatalogPresenceByIdParams = {
    catalogId: number;
    messagingProvider: PubSubProvider;
    subscribeKey: string;
};

type FetchCatalogPresenceByIdPayload = {
    message: string;
    occupancy: number;
    service: 'Presence';
    status: number;
};

/**
 * Gets the current pubnub/ably occupancy count for a catalog
 *
 * @function fetchCatalogPresenceById
 * @category Ably/Pubnub API
 * @param {FetchCatalogPresenceByIdParams}
 * @see https://pubnub.ably.io/v2/presence/sub-key/${subscribeKey}/channel/${channel}
 * @see https://ps.pndsn.com/v2/presence/sub-key/${subscribeKey}/channel/${channel}
 */
export const fetchCatalogPresenceById = ({
    catalogId,
    messagingProvider,
    subscribeKey,
}: FetchCatalogPresenceByIdParams) =>
    new Promise<FetchCatalogPresenceByIdPayload>((resolve, reject) => {
        const request = makeGet({
            apiPath: `v2/presence/sub-key/${subscribeKey}/channel/catalog.${catalogId}`,
            path: messagingProvider === 'ably' ? '<ABLY-API>' : '<PUBNUB-API>',
            queryParams: { disable_uuids: 1 },
            useCacheKey: false,
        });

        request.end((err, response) =>
            handleResponseOld({
                err,
                reject,
                resolve,
                response: { body: response },
                transform: ({ text }: { text: string }) => JSON.parse(text),
            })
        );
    });
